import { ErrorFilled, Information } from '@carbon/react/icons';
import { createDeleteAtom } from 'atoms/create-resource-atom';
import { AtomField, AtomizedForm } from 'atoms/form-atoms';
import {
  Button,
  Column,
  Dropdown,
  Form,
  FormGroup,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  FlexGrid as Grid,
  Row,
  TextArea,
  Tile,
  InlineLoading,
  Layer,
} from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import classNames from 'classnames';
import { Buttons } from 'components/buttons';
import { DatePicker } from 'components/date-picker';
import { DestroyRecordButton } from 'components/destroy-record-button/destroy-record-button';
import { FormErrorList } from 'components/form-error-list';
import { FormRow, FormSection } from 'components/forms/form-section';
import { ImportedTag } from 'components/imported-tag';
import { useAtom, useAtomValue, useStore } from 'jotai';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import type { FieldKeyedErrors } from 'types/api-payloads';
import { EntityResponse, EntityType } from 'types/api-responses';
import { getDateString } from 'utils/dates';
import {
  buildEntityPayloadAtom,
  FormAtoms,
  runMutationAtom,
  useErrorListAtom,
  useFormAtoms,
  useFormHasChangesAtom,
  useFormIsInvalidAtom,
} from './atoms';
import {
  ENTITY_FORM_ERROR_KEY,
  EntitySeedValues,
  flagRationaleValidator,
} from './utils';

export interface EntityFormHandle {
  submit: () => void;
}

import style from './form.module.scss';
import {
  ImportedEntityForm,
  IndividualEntityForm,
  OrganizationEntityForm,
  organizationTypes,
} from './types';
import { resourcesAtom } from 'atoms/root';
import { ContactInfoRows, ContactInfoRowsAtom } from './contact-info-rows';
import { TextField } from './text-field';
import { FLAG_CATEGORIES, OTHER_USER } from './flag-categories';
import type { FlagCategory } from 'types/flags';

const DateField = ({
  id,
  labelText,
  fieldAtom,
}: {
  id: string;
  labelText: string;
  fieldAtom: AtomField<string | null>;
}) => {
  const [field, setField] = useAtom(fieldAtom);

  return (
    <Layer>
      <DatePicker
        id={id}
        size="md"
        labelText={labelText}
        value={field.value ?? undefined}
        onChange={(date) => {
          setField(date[0] ? getDateString(date[0]) : null);
        }}
      />
    </Layer>
  );
};

const TextAreaField = ({
  id,
  labelText,
  fieldAtom,
  rows,
}: {
  id: string;
  labelText: string;
  rows: number;
  fieldAtom: AtomField<string | null>;
}) => {
  const [field, setField] = useAtom(fieldAtom);
  return (
    <Layer>
      <TextArea
        id={id}
        labelText={labelText}
        rows={rows}
        value={field.value || ''}
        onChange={({ target: { value } }) => setField(value)}
      />
    </Layer>
  );
};

const OrganizationType = ({
  fieldAtom,
}: {
  fieldAtom: AtomField<string | null>;
}) => {
  const [field, setField] = useAtom(fieldAtom);
  return (
    <Layer>
      <Dropdown
        titleText={
          <label>
            Organization Type (Required)
            {!field.isValid ? (
              <span className={style.required}>
                <ErrorFilled />
              </span>
            ) : (
              ''
            )}
          </label>
        }
        label=""
        onChange={({ selectedItem }: any) =>
          selectedItem && setField(selectedItem)
        }
        itemToString={(item) => item ?? ''}
        id={'organization-type'}
        items={[...organizationTypes]}
        selectedItem={field.value}
        invalid={!field.isValid && field.isDirty}
        invalidText={!field.isValid && field.isDirty ? field.error : ''}
      />
    </Layer>
  );
};

type FlagItem = { value: FlagCategory | null; label: string };
const NO_FLAG: FlagItem = { value: null, label: 'No flag' };
const flagItems: FlagItem[] = [NO_FLAG, ...FLAG_CATEGORIES];

// The consolidation-risk flag. Lives on the wrapper entity, so it renders once
// for every concrete type. Choosing a category reveals the rationale field;
// Other/User makes it required (validator swapped on change). Surfacing rather
// than hiding is deliberate — see PROD/2907373590 design principles.
const ConsolidationFlag = ({
  categoryAtom,
  rationaleAtom,
}: {
  categoryAtom: AtomField<FlagCategory | null>;
  rationaleAtom: AtomField<string | null>;
}) => {
  const [category, setCategory] = useAtom(categoryAtom);
  const [rationale, setRationale] = useAtom(rationaleAtom);
  const isOtherUser = category.value === OTHER_USER;

  return (
    <>
      <FormRow>
        <Row>
          <Column lg={8} md={4}>
            <Layer>
              <Dropdown<FlagItem>
                id="flag-category"
                titleText="Consolidation-Risk Flag"
                label="No flag"
                items={flagItems}
                itemToString={(item) => item?.label ?? ''}
                selectedItem={
                  flagItems.find((i) => i.value === category.value) ?? NO_FLAG
                }
                onChange={({ selectedItem }) => {
                  const next = selectedItem?.value ?? null;
                  setCategory(next);
                  // Keep the rationale requirement in step with the category.
                  setRationale({ validate: flagRationaleValidator(next) });
                }}
              />
            </Layer>
          </Column>
        </Row>
      </FormRow>
      {category.value && (
        <FormRow>
          <Row>
            <Column lg={16}>
              <Layer>
                <TextArea
                  id="flag-rationale"
                  labelText={
                    isOtherUser ? 'Flag Rationale (Required)' : 'Flag Rationale'
                  }
                  rows={3}
                  value={rationale.value || ''}
                  invalid={!rationale.isValid && rationale.isDirty}
                  invalidText={
                    !rationale.isValid && rationale.isDirty
                      ? rationale.error
                      : ''
                  }
                  onChange={({ target: { value } }) => setRationale(value)}
                />
              </Layer>
            </Column>
          </Row>
        </FormRow>
      )}
    </>
  );
};

const Individual = ({
  form,
  fieldErrors,
  onNameEdit,
}: {
  form: AtomizedForm<IndividualEntityForm>;
  fieldErrors?: FieldKeyedErrors;
  onNameEdit?: () => void;
}) => {
  return (
    <>
      <FormRow>
        <Row className={style.rowThirds}>
          <Column>
            <TextField
              id="firstName"
              labelText="First Name (Required)"
              fieldAtom={form.concreteEntityAttributes.firstName}
              serverError={fieldErrors?.[ENTITY_FORM_ERROR_KEY.firstName]?.[0]}
              onEdit={onNameEdit}
            />
          </Column>
          <Column>
            <TextField
              id="middleName"
              labelText="Middle Name"
              fieldAtom={form.concreteEntityAttributes.middleName}
              serverError={fieldErrors?.[ENTITY_FORM_ERROR_KEY.middleName]?.[0]}
              onEdit={onNameEdit}
            />
          </Column>
          <Column>
            <TextField
              id="lastName"
              labelText="Last Name (Required)"
              fieldAtom={form.concreteEntityAttributes.lastName}
              serverError={fieldErrors?.[ENTITY_FORM_ERROR_KEY.lastName]?.[0]}
              onEdit={onNameEdit}
            />
          </Column>
        </Row>
      </FormRow>
      <FormRow>
        <Row>
          <Column md={4} lg={8}>
            <DateField
              id="date-of-birth"
              labelText="Date of Birth"
              fieldAtom={form.concreteEntityAttributes.birthDate}
            />
          </Column>
          <Column md={4} lg={8}>
            <DateField
              id="date-of-death"
              labelText="Date of Death"
              fieldAtom={form.concreteEntityAttributes.deathDate}
            />
          </Column>
        </Row>
      </FormRow>
      <FormRow>
        <Row>
          <Column lg={16}>
            <TextAreaField
              labelText="Notes"
              rows={5}
              id={`notes`}
              fieldAtom={form.notes}
            />
          </Column>
        </Row>
      </FormRow>
    </>
  );
};

const Organization = ({
  form,
  fieldErrors,
  onNameEdit,
}: {
  form: AtomizedForm<OrganizationEntityForm>;
  fieldErrors?: FieldKeyedErrors;
  onNameEdit?: () => void;
}) => {
  return (
    <>
      <FormRow>
        <Row>
          <Column lg={6} md={4}>
            <OrganizationType
              fieldAtom={form.concreteEntityAttributes.organizationType}
            />
          </Column>
          <Column lg={10} md={4}>
            <TextField
              id="primaryName"
              labelText="Primary Name (Required)"
              fieldAtom={form.concreteEntityAttributes.primaryName}
              serverError={
                fieldErrors?.[ENTITY_FORM_ERROR_KEY.primaryName]?.[0]
              }
              onEdit={onNameEdit}
            />
          </Column>
        </Row>
      </FormRow>
      <FormRow>
        <Row>
          <Column md={4} lg={8}>
            <DateField
              id="formation-date"
              labelText="Formation Date"
              fieldAtom={form.concreteEntityAttributes.formationDate}
            />
          </Column>
          <Column md={4} lg={8}>
            <DateField
              id="termination-date"
              labelText="Termination Date"
              fieldAtom={form.concreteEntityAttributes.terminationDate}
            />
          </Column>
        </Row>
      </FormRow>
      <FormRow>
        <Row>
          <Column lg={16}>
            <TextAreaField
              labelText="Notes"
              rows={5}
              id={`notes`}
              fieldAtom={form.notes}
            />
          </Column>
        </Row>
      </FormRow>
    </>
  );
};

const Imported = ({ form }: { form: AtomizedForm<ImportedEntityForm> }) => {
  return (
    <>
      <FormRow>
        <Row>
          <Column md={4} lg={10}>
            <TextField
              id="name"
              labelText="Name (Required)"
              fieldAtom={form.concreteEntityAttributes.name}
            />
          </Column>
        </Row>
      </FormRow>
      <FormRow>
        <Row>
          <Column lg={16}>
            <TextAreaField
              labelText="Notes"
              rows={5}
              id="imported-entity-notes"
              fieldAtom={form.notes}
            />
          </Column>
        </Row>
      </FormRow>
    </>
  );
};

const DeleteButton = ({ entity }: { entity: EntityResponse | undefined }) => {
  const deleteAtom = useMemo(() => createDeleteAtom(), []);
  const resources = useAtomValue(resourcesAtom);
  const history = useHistory();

  if (!entity) return null;
  if (!entity.auth.includes('destroy')) return null;

  // Temporarily disabled: deleting a dedupe survivor can drop confirmed document parties.
  // The `: boolean` annotation is deliberate — it keeps the render below reachable so
  // TS doesn't drop the `entity` narrowing (and so re-enabling is a one-line flip).
  const deletionEnabled: boolean = false;
  if (!deletionEnabled) return null;

  return (
    <DestroyRecordButton
      deleteAtom={deleteAtom}
      className={style.alignDeleteButtonRight}
      url={entity.href}
      onSuccess={() => {
        const href = resources?.entities.href;
        if (!href) return;
        history.push(href);
      }}
    />
  );
};

const ErrorList = ({ formAtoms }: { formAtoms: FormAtoms }) => {
  const errorListAtom = useErrorListAtom(formAtoms);
  const errorsAtom = useAtomValue(errorListAtom);
  return <FormErrorList errorsAtom={errorsAtom} />;
};

const MainFormButtons = ({
  isUpdatingEntity,
  entity,
  showCancel,
  loading,
  disabled,
  onSubmit,
}: {
  isUpdatingEntity: boolean;
  entity: EntityResponse | undefined;
  showCancel: boolean;
  loading: boolean;
  disabled: boolean;
  onSubmit: () => void;
}) => {
  const history = useHistory();

  return (
    <>
      <Buttons justify="no-margin">
        <Button disabled={disabled || loading} onClick={onSubmit}>
          {isUpdatingEntity ? 'Update Entity' : 'Create Entity'}
        </Button>
        {showCancel && (
          <>
            <div style={{ width: '1rem' }} />
            <Button kind="secondary" onClick={() => history.push('/entities')}>
              Cancel
            </Button>
          </>
        )}
        <DeleteButton entity={entity} />
      </Buttons>
    </>
  );
};

const HasPendingChanges = ({ formAtoms }: { formAtoms: FormAtoms }) => {
  const formHasChangesAtom = useFormHasChangesAtom(formAtoms);
  const hasPendingChanges = useAtomValue(useAtomValue(formHasChangesAtom));
  if (!hasPendingChanges) return null;

  return (
    <InlineNotification
      kind="info"
      lowContrast
      title="Unsaved changes"
      hideCloseButton={true}
    />
  );
};

interface EntityFormProps {
  entity: EntityResponse | undefined;
  getEntity: (() => Promise<void>) | undefined;
  loading: boolean;
  entityTypeValue?: EntityType;
  onSuccess?: (entity: EntityResponse) => void;
  showCancel?: boolean;
  seedValues?: EntitySeedValues;
  hideSubmitButton?: boolean;
  onValidityChange?: (valid: boolean) => void;
  // When provided, submit hands the assembled `{ concreteEntityType, … }`
  // payload to the parent instead of POSTing to /entities. Used by the
  // Canonize panel so the entity attributes and the merge travel in one
  // atomic request to the canonization endpoint.
  onSubmitPayload?: (payload: Record<string, unknown>) => void;
  // Single-column layout: stacks Demographic → Address → Phone → Email full
  // width instead of the default two-column grid. Used in narrow side panels
  // (Canonize) where the two-column layout crowds the contact fields.
  stacked?: boolean;
  // Server errors keyed by field, shown on their inputs. A name collision
  // describes the submitted name, so onFieldErrorsStale fires once the
  // examiner edits any name field (Individuals match first and last in either
  // order) or switches the entity type; the parent clears them.
  fieldErrors?: FieldKeyedErrors;
  onFieldErrorsStale?: () => void;
}

const EntityForm = forwardRef<EntityFormHandle, EntityFormProps>(
  (
    {
      entity,
      getEntity,
      loading,
      entityTypeValue = 'IndividualEntity',
      onSuccess,
      showCancel = true,
      seedValues,
      hideSubmitButton = false,
      onValidityChange,
      onSubmitPayload,
      stacked = false,
      fieldErrors,
      onFieldErrorsStale,
    },
    ref
  ) => {
    const [entityType, setEntityType] = useState(entityTypeValue);
    const formAtoms = useFormAtoms(entity, entityType, seedValues);
    const formObj = useAtomValue(formAtoms);
    const store = useStore();
    const payloadAtom = useMemo(
      () => buildEntityPayloadAtom(formAtoms),
      [formAtoms]
    );
    const isImportedEntity = entity?.type === 'ImportedEntity';

    const isUpdatingEntity = !!entity;

    const [entityCreation, createEntity] = useAtom(runMutationAtom);
    const formIsInvalidAtom = useFormIsInvalidAtom(formAtoms);
    const formIsInvalid = useAtomValue(useAtomValue(formIsInvalidAtom));
    const history = useHistory();

    useEffect(() => {
      onValidityChange?.(!formIsInvalid);
    }, [formIsInvalid, onValidityChange]);

    const submit = useCallback(() => {
      // Deferred-submit mode: hand the assembled attributes to the parent
      // instead of creating the entity here, so the caller can bundle them
      // into a single atomic request (Canonize).
      if (onSubmitPayload) {
        onSubmitPayload(store.get(payloadAtom));
        return;
      }

      createEntity({
        isUpdatingEntity,
        formAtoms,
        entityUri: entity?.href,
        onError: () => void 0,
        onSuccess: (saved) => {
          if (onSuccess) {
            onSuccess(saved);
            return;
          }

          if (isUpdatingEntity) {
            getEntity?.();
          } else {
            history.push(saved.href);
          }
        },
      });
    }, [
      onSubmitPayload,
      store,
      payloadAtom,
      createEntity,
      isUpdatingEntity,
      formAtoms,
      entity?.href,
      onSuccess,
      getEntity,
      history,
    ]);

    useImperativeHandle(ref, () => ({ submit }), [submit]);

    const demographicTile = (
      <Tile className={style.tileSection}>
        <ImportedTag value={isImportedEntity} />
        {!isImportedEntity ? (
          <FormSection title="Demographic Information" iconComponent={''}>
            <FormGroup legendText="Entity Type">
              <FormRow>
                <RadioButtonGroup
                  name="entityType"
                  valueSelected={entityType}
                  onChange={(value) => {
                    setEntityType(value as EntityType);
                    onFieldErrorsStale?.();
                  }}
                  disabled={isUpdatingEntity}
                >
                  <RadioButton
                    labelText={
                      <span className={style.radioLabelWithTooltip}>
                        Individual
                        <TooltipIcon
                          definition="Individual - A natural person identified by a personal name"
                          align="top-start"
                          className={style.radioTooltip}
                        >
                          <Information size={16} />
                        </TooltipIcon>
                      </span>
                    }
                    value="IndividualEntity"
                  />
                  <RadioButton
                    labelText={
                      <span className={style.radioLabelWithTooltip}>
                        Organization
                        <TooltipIcon
                          definition="Organization - A legally formed entity identified by a registered business or legal name"
                          align="top-start"
                          className={style.radioTooltip}
                        >
                          <Information size={16} />
                        </TooltipIcon>
                      </span>
                    }
                    value="OrganizationEntity"
                  />
                </RadioButtonGroup>
              </FormRow>
              {formObj.type === 'IndividualEntity' && (
                <Individual
                  form={formObj.form}
                  fieldErrors={fieldErrors}
                  onNameEdit={onFieldErrorsStale}
                />
              )}
              {formObj.type === 'OrganizationEntity' && (
                <Organization
                  form={formObj.form}
                  fieldErrors={fieldErrors}
                  onNameEdit={onFieldErrorsStale}
                />
              )}
            </FormGroup>
          </FormSection>
        ) : (
          <FormSection title="Demographic Information" iconComponent={''}>
            {formObj.type === 'ImportedEntity' && (
              <Imported form={formObj.form} />
            )}
          </FormSection>
        )}
        <FormSection title="Consolidation-Risk Flag" iconComponent={''}>
          <ConsolidationFlag
            categoryAtom={formObj.form.flagCategory}
            rationaleAtom={formObj.form.flagRationale}
          />
        </FormSection>
      </Tile>
    );

    const addressTile = (
      <Tile className={style.tileSection}>
        <ContactInfoRows
          form={formObj.form.addressesAttributes as ContactInfoRowsAtom}
          title="Address Information"
          addLabel="Add New Address"
          type="address"
        />
      </Tile>
    );

    const emailTile = (
      <Tile className={style.tileSection}>
        <ContactInfoRows
          form={formObj.form.emailAddressesAttributes as ContactInfoRowsAtom}
          title="E-mail Information"
          addLabel="Add New Email"
          type="email"
        />
      </Tile>
    );

    const phoneTile = (
      <Tile className={style.tileSection}>
        <ContactInfoRows
          form={formObj.form.phoneNumbersAttributes as ContactInfoRowsAtom}
          title="Phone Information"
          addLabel="Add New Phone"
          type="phone"
        />
      </Tile>
    );

    return (
      <Tile className={style.tileEntities}>
        <Form
          title=""
          className={classNames(style.form, style.anchorSection)}
          id="demographic-information"
        >
          {loading ? (
            <InlineLoading />
          ) : (
            <Grid className="bx--no-gutter" fullWidth>
              {stacked ? (
                // Single column: contact info stacks below address info so a
                // narrow side panel doesn't crowd the fields into a half-width
                // right rail.
                <Row>
                  <Column lg={16}>
                    {demographicTile}
                    {addressTile}
                    {phoneTile}
                    {emailTile}
                  </Column>
                </Row>
              ) : (
                <Row>
                  <Column md={8} lg={9}>
                    {demographicTile}
                    {addressTile}
                  </Column>
                  <Column md={8} lg={7}>
                    {emailTile}
                    {phoneTile}
                  </Column>
                </Row>
              )}
              <Row>
                <Column lg={16}>
                  <Tile>
                    <ErrorList formAtoms={formAtoms} />
                    <HasPendingChanges formAtoms={formAtoms} />
                    {!hideSubmitButton && (
                      <MainFormButtons
                        showCancel={showCancel}
                        isUpdatingEntity={isUpdatingEntity}
                        entity={entity}
                        loading={entityCreation.loading}
                        disabled={formIsInvalid}
                        onSubmit={submit}
                      />
                    )}
                  </Tile>
                </Column>
              </Row>
            </Grid>
          )}
        </Form>
      </Tile>
    );
  }
);

EntityForm.displayName = 'EntityForm';

export { EntityForm };
